import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faTriangleExclamation,
  faSimCard,
  faCopy,
  faCheck,
  faHeadset,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { getMyEsims } from './accountApi';
import { statusUi, isReadyToUse, isStatusUnavailable } from './esimStatus';
import { formatData as formatDataBase, formatDate as formatDateBase } from '../../utils/formatters';
import './account.css';

const WHATSAPP = '436765551333';
const SUPPORT_EMAIL = 'esim@utkanesim.com';

// Bu ekran GB'yi iki ondalıkla ve tarihi saat bilgisiyle gösterir.
const formatData = (mb) => formatDataBase(mb, { gbDigits: 2 });
const formatDate = (value, locale) => formatDateBase(value, locale, { withTime: true });

const CopyRow = ({ label, value }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* yoksa sessiz geç */
    }
  };
  return (
    <div className="tech-row">
      <div className="tech-row-info">
        <span className="tech-row-label">{label}</span>
        <span className="tech-row-value">{value}</span>
      </div>
      <button type="button" className="tech-copy" onClick={copy} aria-label={t('order.copy')}>
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      </button>
    </div>
  );
};

const DataRing = ({ remaining, total }) => {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const used = total > remaining ? total - remaining : 0;

  return (
    <div className="data-usage">
      <svg className="data-ring" viewBox="0 0 130 130" width="150" height="150">
        <circle cx="65" cy="65" r={r} className="data-ring-bg" />
        <circle
          cx="65"
          cy="65"
          r={r}
          className="data-ring-fill"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
          transform="rotate(-90 65 65)"
        />
      </svg>
      <div className="data-ring-center">
        <span className="data-ring-value">{formatData(remaining)}</span>
        <span className="data-ring-label">{t('esimDetail.left')}</span>
      </div>
      <div className="data-legend">
        <span><i className="dot dot-cyan" />{t('esimDetail.leftData')}<b>{formatData(remaining)}</b></span>
        <span><i className="dot dot-orange" />{t('esimDetail.usedData')}<b>{formatData(used)}</b></span>
        <span><i className="dot dot-muted" />{t('esimDetail.totalData')}<b>{formatData(total)}</b></span>
      </div>
    </div>
  );
};

const PurchasedEsimDetail = () => {
  const { t, i18n } = useTranslation();
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [state, setState] = useState(
    location.state?.esim ? { esim: location.state.esim } : { loading: true }
  );

  // State yoksa (yenileme/doğrudan erişim) listeyi çekip orderId ile bul.
  useEffect(() => {
    if (state.esim || state.error) return undefined;
    let cancelled = false;
    getMyEsims()
      .then((list) => {
        if (cancelled) return;
        const found = (list || []).find((e) => e.orderId === orderId);
        setState(found ? { esim: found } : { error: t('home.error.title') });
      })
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [orderId, state.esim, state.error, t]);

  const esim = state.esim;
  const status = esim
    ? statusUi(esim)
    : { key: 'esimStatus.unavailable', cls: 'unknown' };

  // geçerlilik metni — durum kararı displayStatus'tan (bkz. esimStatus.js).
  let validityText = '—';
  if (esim) {
    if (isReadyToUse(esim)) {
      // Süre ilk kullanımla başlar; esimExpiresAt bu durumda null gelir.
      validityText = t('esimDetail.notYetActive');
    } else if (esim.esimExpiresAt) {
      const days = Math.ceil((new Date(esim.esimExpiresAt) - new Date()) / 86400000);
      validityText = days > 0 ? t('esimDetail.daysLeft', { days }) : t('esimDetail.expired');
    } else if (isStatusUnavailable(esim)) {
      validityText = t('esimStatus.unavailable');
    }
  }

  const waUrl = esim
    ? `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
        `${t('whatsapp.message')}${esim.esimIccid ? ` ICCID: ${esim.esimIccid}` : ''}`
      )}`
    : `https://wa.me/${WHATSAPP}`;
  const mailUrl = esim
    ? `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        `eSIM destek — ${esim.esimIccid || esim.orderId}`
      )}`
    : `mailto:${SUPPORT_EMAIL}`;

  return (
    <div className="account">
      <StoreHeader />
      <main className="account-main account-main--narrow">
        <button type="button" className="esim-detail-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('esimDetail.back')}
        </button>

        {state.loading && (
          <div className="acc-state"><FontAwesomeIcon icon={faSpinner} spin size="2x" /></div>
        )}

        {state.error && (
          <div className="acc-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{state.error}</p>
          </div>
        )}

        {esim && (
          <div className="acc-stack">
            {/* Başlık */}
            <div className="esim-detail-head">
              <span className="esim-detail-icon"><FontAwesomeIcon icon={faSimCard} /></span>
              <div>
                <h1 className="esim-detail-name">
                  {esim.providerBundleName || esim.clientProductId}
                </h1>
                <span className={`esim-detail-status status--${status.cls}`}>
                  {t(status.key)}
                </span>
              </div>
            </div>

            {/* Veri kullanımı */}
            {esim.totalDataMb != null && esim.totalDataMb > 0 && (
              <div className="acc-card">
                <h2 className="acc-card-title">{t('esimDetail.dataUsage')}</h2>
                <DataRing remaining={esim.remainingDataMb ?? 0} total={esim.totalDataMb} />
              </div>
            )}

            {/* QR kodu */}
            {esim.qrCodeData && (
              <div className="acc-card esim-detail-qr-card">
                <h2 className="acc-card-title">{t('order.qrTitle')}</h2>
                <p className="acc-meta">{t('esimDetail.qrDesc')}</p>
                <div className="esim-detail-qr">
                  <QRCodeSVG value={esim.qrCodeData} size={196} level="M" includeMargin />
                </div>
              </div>
            )}

            {/* Geçerlilik */}
            <div className="acc-card">
              <h2 className="acc-card-title">{t('esimDetail.validity')}</h2>
              <div className="info-rows">
                <div className="info-row">
                  <span>{t('esimDetail.leftDate')}</span>
                  <span className="info-row-strong">{validityText}</span>
                </div>
                <div className="info-row">
                  <span>{t('esimDetail.activationDate')}</span>
                  <span>{formatDate(esim.esimActivatedAt, i18n.language)}</span>
                </div>
                <div className="info-row">
                  <span>{t('esimDetail.endDate')}</span>
                  <span>{formatDate(esim.esimExpiresAt, i18n.language)}</span>
                </div>
              </div>
            </div>

            {/* Teknik bilgiler */}
            <div className="acc-card">
              <h2 className="acc-card-title">{t('esimDetail.technical')}</h2>
              <div className="tech-rows">
                <CopyRow label={t('esimDetail.activationCode')} value={esim.qrCodeData} />
                <CopyRow label="ICCID" value={esim.esimIccid} />
                <CopyRow label={t('esimDetail.smdp')} value={esim.smdpAddress} />
                <CopyRow label={t('esimDetail.matchingId')} value={esim.matchingId} />
              </div>
            </div>

            {/* Teknik destek */}
            <div className="acc-card">
              <h2 className="acc-card-title">
                <FontAwesomeIcon icon={faHeadset} /> {t('esimDetail.support')}
              </h2>
              <p className="acc-meta">{t('esimDetail.supportDesc')}</p>
              <div className="esim-detail-support">
                <a className="support-btn support-wa" href={waUrl} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
                </a>
                <a className="support-btn support-mail" href={mailUrl}>
                  <FontAwesomeIcon icon={faEnvelope} /> {t('esimDetail.email')}
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PurchasedEsimDetail;
